import clsx from 'clsx'
import GridSpinner from 'components/UI/GridSpinner'
import { Button, Col, Container, Grid, Icon, Row, Typography } from 'gu-ui-kit'
import CampaignStartForm from 'modules/Forms/Account/Campaign/Start'
import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { Link, useRouteMatch } from 'react-router-dom'
import formatMoney from 'utils/formatMoney'
import { useCancelToken } from 'utils/hooks/useCancelToken'
import useNetworkPalette from 'utils/hooks/useNetworkPalette'
import ActionLink from 'modules/Services/Account/subcomponents/ActionLink'
import servicesCommonStyles from 'modules/Services/services.module.scss'
import styles from 'modules/Services/Campaigns/Main/network-account-campaign.module.scss'
import Campaign from '../Main/subcomponents/List/Campaign'
import campaignStartStyles from 'modules/Services/Campaigns/Start/campaign-start.module.scss'
import { ROUTES } from 'constants/routes'
import {
  FETCH_NETWORK_ACCOUNT,
  YANDEX_SUBSCRIPTION_CAMPAIGN_FETCH,
} from 'modules/Services/types'

const CampaignStart = () => {
  const pathGen = useGeneratePathI18n()
  const dispatch = useDispatch()
  const {
    params: { networkType, accountId, campaignId },
  } = useRouteMatch()

  const { t } = useTranslation(['services'])
  const { cancelableDispatch } = useCancelToken()

  const {
    networkAccount: { login, status, balance, main_action },
    i18n,
    campaign,
  } = useSelector((state) => ({
    networkAccount: state['services/network-account'],
    i18n: state['app'].i18n,
    campaign: state['services/campaign'].campaign,
  }))

  const paletteClass = useNetworkPalette(networkType)

  useEffect(
    () =>
      dispatch({
        type: YANDEX_SUBSCRIPTION_CAMPAIGN_FETCH,
        payload: {
          networkType,
          accountId,
          campaignId,
        },
      }),
    [],
  )

  useEffect(() => {
    cancelableDispatch({
      type: FETCH_NETWORK_ACCOUNT,
      payload: { accountId },
    })
  }, [accountId])

  return (
    <>
      <Helmet defer={false}>
        <title>
          {t(`services::networks.${networkType}`)}
          {' — '}
          {t(`common::nav_menu.network_account_campaign_create`)}
        </title>
      </Helmet>

      <Grid className={styles['network-account__grid-container']}>
        <Row justifyContent="center">
          <Col sm="12" lg="6" className={styles['network-account__column']}>
            <Button
              component={Link}
              to={pathGen(ROUTES.SERVICES_NETWORK, {
                networkType,
              })}
              startIcon={<Icon name={'back'} size={'l'} />}
              className={styles['network-account__back-button']}
            />
            <div className={styles['network-account__container']}>
              <div
                className={clsx(
                  'bg-network-' + networkType + '-static',
                  styles['network-account__head'],
                  servicesCommonStyles['palette_' + paletteClass],
                )}
              >
                <Link
                  to={pathGen(ROUTES.SERVICES_NETWORK, {
                    networkType,
                  })}
                >
                  {t(`services::networks.${networkType}`)}
                </Link>
              </div>

              <div className={styles['network-account__description']}>
                {balance.amount === null ? (
                  <GridSpinner color={'inherit'} />
                ) : (
                  <>
                    <div>
                      <Container
                        display={'flex'}
                        flexJustify={'space-between'}
                        flexAlign={'flex-start'}
                        flexWrap={'wrap'}
                      >
                        <div
                          className={styles['network-account__login-container']}
                        >
                          <Typography
                            variant="p5"
                            className={styles['network-account__login']}
                          >
                            {login}
                            {status === 'deleted' && (
                              <span
                                className={
                                  styles['network-account__deleted-label']
                                }
                              >
                                ({' '}
                                {t(
                                  `services::account_list.statuses.${status}`,
                                ).toLowerCase()}{' '}
                                )
                              </span>
                            )}
                          </Typography>
                        </div>
                      </Container>
                    </div>
                    <Typography
                      variant="h5"
                      component={
                        <span
                          dangerouslySetInnerHTML={{
                            __html: formatMoney(
                              balance.amount,
                              balance.currency,
                            ),
                          }}
                        />
                      }
                    />
                  </>
                )}

                <Container display={'flex'} flexWrap={'wrap'}>
                  {!!main_action && (
                    <ActionLink
                      action={main_action}
                      i18n={i18n}
                      id={accountId}
                      type={networkType}
                      balance={balance}
                    />
                  )}
                </Container>
                <Container
                  className={campaignStartStyles['campaign__description']}
                >
                  {campaign?.id ? (
                    <>
                      <Typography variant={'p7'} color={'text-secondary'}>
                        {t(`services::campaign.launch.header`)}
                      </Typography>
                      <Campaign {...campaign} infoOnly={true} />
                    </>
                  ) : (
                    <Container
                      className={campaignStartStyles['spinner-container']}
                    >
                      <GridSpinner width={32} style={{ height: '100%' }} />
                    </Container>
                  )}
                </Container>
              </div>
            </div>

            <div
              className={clsx(
                styles['network-account__container'],
                campaignStartStyles['container'],
              )}
            >
              <CampaignStartForm promoCode={campaign?.promocode} />
            </div>
          </Col>
        </Row>
      </Grid>
    </>
  )
}

export default CampaignStart
